/* One shared skin; Enterprise alone owns disclosure, focus, links and touch behavior. */
(() => {
  'use strict';
  if (window.BiciLiquidNavigation) return;
  const controllers = new WeakMap();
  const selector = 'store-header[data-liquid-navigation="true"]';
  const spring = 'cubic-bezier(.22,.85,.22,1)';
  const closeEnough = (a, b) => a && b && ['x', 'y', 'width', 'height'].every(k => Math.abs(a[k] - b[k]) < .5);

  class LiquidNavigation {
    constructor(header) {
      this.header = header;
      this.nav = header.querySelector('.main-nav');
      if (!this.nav) return;
      this.menu = this.nav.closest('main-menu');
      this.originalSensitivity = this.menu.dataset.menuSensitivity;
      this.desktop = matchMedia('(min-width: 769px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.abort = new AbortController();
      this.contentAnimations = [];
      this.panel = null;
      this.hoverRow = null;
      this.frame = 0;
      this.closeTimer = 0;
      this.destroyed = false;
      this.rows = Array.from(this.nav.children).map(li => li.querySelector(':scope > .main-nav__row')).filter(Boolean);
      this.panels = Array.from(this.nav.children).map(li => li.querySelector(':scope > .main-nav__child')).filter(Boolean);
      this.panels.forEach(panel => {
        const count = panel.querySelectorAll('.child-nav > li:not(.md\\:hidden)').length;
        panel.style.setProperty('--bici-panel-width', count <= 4 ? '560px' : count <= 12 ? '980px' : '1120px');
      });
      this.pill = this.createSkin('bici-liquid-nav-pill');
      this.surface = this.createSkin('bici-liquid-nav-surface');
      const options = { signal: this.abort.signal };
      this.nav.addEventListener('pointerover', event => {
        if (event.pointerType === 'touch') return;
        const row = event.target.closest('.main-nav__row');
        if (!this.rows.includes(row) || row === this.hoverRow) return;
        this.hoverRow = row;
        if (this.desktop.matches && !row.parentElement.querySelector(':scope > .main-nav__child')) {
          this.header.dataset.liquidLeaf = 'true';
          // Use Enterprise's own close path immediately for a tab without a dropdown.
          this.menu.handleClose?.();
        }
        this.schedule();
      }, options);
      this.nav.addEventListener('pointerleave', () => { this.hoverRow = null; this.schedule(); }, options);
      header.addEventListener('focusin', event => {
        const item = event.target.closest('.child-nav > li');
        this.contentAnimations.forEach(animation => { if (animation.effect?.target === item) animation.cancel(); });
        this.schedule();
      }, options);
      header.addEventListener('focusout', () => this.schedule(), options);
      this.onMediaChange = () => this.resetMode();
      this.desktop.addEventListener('change', this.onMediaChange);
      this.reduced.addEventListener('change', this.onMediaChange);
      this.mutations = new MutationObserver(() => this.schedule());
      this.mutations.observe(this.nav, { subtree: true, attributes: true, attributeFilter: ['class'] });
      this.resize = new ResizeObserver(() => this.schedule());
      [header, this.nav, ...this.panels].forEach(e => this.resize.observe(e));
      this.resetMode();
    }

    createSkin(className) {
      const skin = document.createElement('span');
      skin.className = className;
      skin.setAttribute('aria-hidden', 'true');
      this.header.append(skin);
      return skin;
    }

    schedule() {
      if (this.frame || this.destroyed) return;
      this.frame = requestAnimationFrame(() => { this.frame = 0; this.sync(); });
    }

    resetMode() {
      this.menu.dataset.menuSensitivity = this.desktop.matches ? '110' : this.originalSensitivity;
      this.cancelAnimations();
      clearTimeout(this.closeTimer);
      this.closeTimer = 0;
      this.panel = null;
      this.surfaceRect = null;
      this.pillRect = null;
      this.hoverRow = null;
      this.header.dataset.liquidOpen = 'false';
      this.header.dataset.liquidPill = 'false';
      this.header.dataset.liquidLeaf = 'false';
      delete this.header.dataset.liquidPanel;
      this.header.classList.toggle('bici-liquid-ready', this.desktop.matches);
      this.schedule();
    }

    sync() {
      if (!this.desktop.matches || this.destroyed) return;
      const visible = this.panels.find(panel => panel.parentElement.classList.contains('is-visible') && panel.parentElement.classList.contains('is-open'));
      // Enterprise opens the next disclosure over two rAFs. Keep the shared skin alive.
      const opening = this.panels.find(panel => panel !== this.panel && panel.parentElement.classList.contains('is-open'));
      const leafIntent = this.hoverRow && !this.hoverRow.parentElement.querySelector(':scope > .main-nav__child');
      const active = leafIntent ? null : visible || opening;
      const focusRow = this.rows.find(row => row.contains(document.activeElement));
      const row = this.hoverRow || focusRow || active?.previousElementSibling;
      const headerRect = this.header.getBoundingClientRect();
      // All layout reads precede writes. No scroll listener and no perpetual frame loop.
      const rowRect = row?.getBoundingClientRect();
      const panelRect = active?.getBoundingClientRect();
      const oldPill = this.pill.getBoundingClientRect();
      const oldSurface = this.surface.getBoundingClientRect();
      const pillOpacity = getComputedStyle(this.pill).opacity;
      this.header.dataset.liquidLeaf = leafIntent ? 'true' : 'false';
      if (rowRect?.width) {
        const padding = innerWidth < 1200 ? 6 : 10;
        this.movePill({ x: rowRect.x - headerRect.x - padding, y: rowRect.y - headerRect.y + (rowRect.height - 36) / 2, width: rowRect.width + padding * 2, height: 36 }, oldPill, headerRect, pillOpacity);
      } else {
        this.pillAnimation?.cancel();
        this.header.dataset.liquidPill = 'false';
      }
      if (active && panelRect?.height) {
        clearTimeout(this.closeTimer);
        this.closeTimer = 0;
        const rect = { x: panelRect.x - headerRect.x, y: panelRect.y - headerRect.y, width: panelRect.width, height: panelRect.height };
        const changed = this.panel !== active;
        this.moveSurface(rect, oldSurface, headerRect);
        this.panel = active;
        this.header.dataset.liquidPanel = active.id;
        if (changed) this.revealContent(active);
      } else if (leafIntent && this.panel) {
        clearTimeout(this.closeTimer);
        this.closeTimer = 0;
        this.closeSurface(true);
      } else if (this.panel && !this.closeTimer) {
        this.closeTimer = setTimeout(() => { this.closeTimer = 0; this.closeSurface(); }, 90);
      }
    }

    setRect(prefix, rect) {
      for (const [key, value] of Object.entries(rect)) this.header.style.setProperty(`--bici-${prefix}-${key}`, `${value}px`);
    }

    movePill(rect, previous, headerRect, previousOpacity) {
      const wasVisible = this.header.dataset.liquidPill === 'true';
      this.header.dataset.liquidPill = 'true';
      if (wasVisible && closeEnough(rect, this.pillRect)) return;
      this.pillAnimation?.cancel();
      this.setRect('pill', rect);
      this.pillRect = rect;
      if (this.reduced.matches) return;
      const from = wasVisible && previous.width ? `translate(${previous.x - headerRect.x - rect.x}px,${previous.y - headerRect.y - rect.y}px) scale(${previous.width / rect.width},${previous.height / rect.height})` : 'scale(.92)';
      this.pillAnimation = this.pill.animate([
        { transform: from, opacity: wasVisible ? previousOpacity : 0 },
        { transform: 'none', opacity: 1 }
      ], { duration: wasVisible ? 380 : 220, easing: spring });
    }

    moveSurface(rect, previous, headerRect) {
      const wasOpen = this.header.dataset.liquidOpen === 'true';
      if (wasOpen && closeEnough(rect, this.surfaceRect)) return;
      this.surfaceAnimation?.cancel();
      this.setRect('surface', rect);
      this.surfaceRect = rect;
      this.header.dataset.liquidOpen = 'true';
      if (this.reduced.matches) return;
      const frames = wasOpen && previous.width ? [
        { transform: `translate(${previous.x - headerRect.x - rect.x - (rect.width - previous.width) / 2}px,${previous.y - headerRect.y - rect.y}px) scale(${previous.width / rect.width},${previous.height / rect.height})`, opacity: 1 },
        { transform: 'scale(1.004,1.012)', borderRadius: '27px 27px 23px 23px', opacity: 1, offset: .78 },
        { transform: 'none', borderRadius: '24px', opacity: 1 }
      ] : [
        { transform: 'translateY(-8px) scale(.92,.58)', borderRadius: '28px 28px 45% 45% / 28px 28px 36% 36%', opacity: 0 },
        { transform: 'scale(1.004,1.025)', borderRadius: '26px 26px 24px 24px', opacity: 1, offset: .74 },
        { transform: 'none', borderRadius: '24px', opacity: 1 }
      ];
      this.surfaceAnimation = this.surface.animate(frames, { duration: wasOpen ? 420 : 460, easing: spring });
    }

    revealContent(panel) {
      this.contentAnimations.forEach(animation => animation.cancel());
      this.contentAnimations = [];
      if (this.reduced.matches) return;
      const list = panel.querySelector('.child-nav');
      if (!list) return;
      const columns = getComputedStyle(list).gridTemplateColumns.split(' ').length;
      const items = Array.from(list.children).filter(item => item.getBoundingClientRect().width);
      this.contentAnimations = items.map((item, index) => item.animate([
        { opacity: 0, transform: 'translateY(10px) scale(.985)' },
        { opacity: 1, transform: 'none' }
      ], { duration: 280, delay: Math.min(108, (index % columns) * 18 + Math.floor(index / columns) * 12), easing: spring, fill: 'backwards' }));
    }

    closeSurface(immediateIntent = false) {
      if (!immediateIntent && this.panels.some(panel => panel.parentElement.classList.contains('is-visible') && panel.parentElement.classList.contains('is-open'))) { this.schedule(); return; }
      const style = getComputedStyle(this.surface);
      const from = { opacity: style.opacity, transform: style.transform };
      this.surfaceAnimation?.cancel();
      this.header.dataset.liquidOpen = 'false';
      delete this.header.dataset.liquidPanel;
      this.panel = null;
      if (!this.reduced.matches) this.surfaceAnimation = this.surface.animate([from, { opacity: 0, transform: 'translateY(-5px) scale(.985,.94)' }], { duration: immediateIntent ? 70 : 160, easing: 'ease-out' });
      this.schedule();
    }

    cancelAnimations() {
      this.pillAnimation?.cancel();
      this.surfaceAnimation?.cancel();
      this.contentAnimations.forEach(animation => animation.cancel());
      this.contentAnimations = [];
    }

    destroy() {
      this.menu.dataset.menuSensitivity = this.originalSensitivity;
      this.destroyed = true;
      this.abort.abort();
      clearTimeout(this.closeTimer);
      cancelAnimationFrame(this.frame);
      this.mutations.disconnect();
      this.resize.disconnect();
      this.desktop.removeEventListener('change', this.onMediaChange);
      this.reduced.removeEventListener('change', this.onMediaChange);
      this.cancelAnimations();
      this.pill.remove();
      this.surface.remove();
      this.header.classList.remove('bici-liquid-ready');
      ['liquidOpen', 'liquidPanel', 'liquidPill', 'liquidLeaf'].forEach(key => delete this.header.dataset[key]);
      this.panels.forEach(panel => panel.style.removeProperty('--bici-panel-width'));
      ['pill', 'surface'].forEach(prefix => ['x', 'y', 'width', 'height'].forEach(key => this.header.style.removeProperty(`--bici-${prefix}-${key}`)));
    }
  }

  const headersWithin = root => [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
  const init = root => headersWithin(root).forEach(header => {
    if (!controllers.has(header) && header.querySelector('.main-nav')) controllers.set(header, new LiquidNavigation(header));
  });
  window.BiciLiquidNavigation = { init };
  customElements.whenDefined('main-menu').then(() => init(document));
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => headersWithin(event.target).forEach(header => { controllers.get(header)?.destroy(); controllers.delete(header); }));
})();
